import React, { useContext, createContext, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import CircleLeftArrow from "../../Components/Icons/CircleLeftArrow";
import CircleRightArrow from "../../Components/Icons/CircleRightArrow";
import { useTranslation } from "react-i18next";
import Settings from "../../Components/Icons/Settings";
import { useSidebar } from "../../Context/SidebarContext";

const LocalSettingsContext = createContext();

export default function SettingsSidebar({ children }) {
  const [expanded, setExpanded] = useState(true);
  const { mobileOpen, closeMobile } = useSidebar();
  const { t, i18n } = useTranslation();

  function getExpand() {
    let locExp = localStorage.getItem("settingsExpand");
    if (locExp) {
      setExpanded(JSON.parse(locExp));
    } else {
      localStorage.setItem("settingsExpand", true);
      setExpanded(true);
    }
  }

  useEffect(() => {
    getExpand();
  }, []);

  function toggleExpanded(e) {
    setExpanded(e);
    localStorage.setItem("settingsExpand", e);
  }

  return (
    <>
      {/* Desktop Settings Sidebar (visible on lg: >= 1024px) */}
      <aside className="h-full z-[999] hidden lg:block">
        <nav className="h-full flex flex-col bg-white border-r shadow-sm">
          <div className="p-4 pb-2 flex justify-between items-center">
            <div
              className={`overflow-hidden font-medium text-xl text-baseColor-primary text-center transition-all ${expanded ? "w-32" : "w-0"
                }`}
            >
              nutrade
            </div>
            <button
              onClick={() => toggleExpanded(!expanded)}
              className="p-1.5 rounded-lg bg-gray-50 hover:bg-gray-100 cursor-pointer"
            >
              {expanded ? <CircleLeftArrow /> : <CircleRightArrow />}
            </button>
          </div>

          <div
            className="w-full flex py-1 xl:py-2 px-6 font-medium my-1 items-center overflow-hidden transition-all text-xs 2xl:text-sm"
            dir={i18n.language == "ar" ? "rtl" : ""}
          >
            {expanded ? "Settings :" : <Settings color="black" />}
          </div>

          <LocalSettingsContext.Provider value={{ expanded, isMobileDrawer: false }}>
            <ul
              className="flex-1 px-3 overflow-y-scroll"
              dir={i18n.language == "ar" ? "rtl" : ""}
            >
              {children}
            </ul>
          </LocalSettingsContext.Provider>

          <div className="w-full px-4 pb-3">
            <p
              className={`font-normal text-left ${expanded ? "text-xs" : "text-[10px]"
                } text-slate-400`}
            >
              <a href="https://numota.in" target="_blank" rel="noreferrer">
                {expanded && <span>&#169;</span>} numota
              </a>
            </p>
          </div>
        </nav>
      </aside>

      {/* Mobile Settings Drawer Slider (visible on < 1024px) */}
      <div className="lg:hidden">
        {mobileOpen && (
          <div
            className="fixed inset-0 bg-slate-900/50 backdrop-blur-xs z-[9998] transition-opacity duration-300"
            onClick={closeMobile}
          />
        )}

        <div
          className={`fixed top-0 left-0 bottom-0 w-[300px] max-w-[85vw] bg-white z-[9999] shadow-2xl flex flex-col transition-transform duration-300 ease-in-out ${mobileOpen ? "translate-x-0" : "-translate-x-full"
            }`}
        >
          <div className="p-4 flex items-center justify-between border-b border-slate-100">
            <button
              onClick={closeMobile}
              className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-800 transition-colors focus:outline-none cursor-pointer"
              aria-label="Close drawer"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
            <div className="font-semibold text-xl text-baseColor-primary">
              Settings
            </div>
            <div className="w-6" />
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-3">
            <LocalSettingsContext.Provider
              value={{ expanded: true, isMobileDrawer: true, closeMobile }}
            >
              <ul className="space-y-1" dir={i18n.language == "ar" ? "rtl" : ""}>
                {children}
              </ul>
            </LocalSettingsContext.Provider>
          </div>

          <div className="w-full px-4 pb-4">
            <p className="font-normal text-xs text-slate-400 text-left">
              <a href="https://numota.in" target="_blank" rel="noreferrer">
                <span>&#169;</span> numota
              </a>
            </p>
          </div>
        </div>
      </div>
    </>
  );
}

export function SettingsSidebarItem({ icon, text, active, alert, link }) {
  const { expanded, isMobileDrawer, closeMobile } = useContext(LocalSettingsContext);
  const { t, i18n } = useTranslation();

  const handleMobileClick = () => {
    if (isMobileDrawer && closeMobile) {
      closeMobile();
    }
  };

  if (isMobileDrawer) {
    return (
      <li className="list-none my-1">
        <Link to={link} onClick={handleMobileClick}>
          <div
            className={`
              relative flex items-center px-3 py-1.5
              font-medium rounded-xl border cursor-pointer
              transition-all duration-150 shadow-xs
              ${active
                ? "bg-[#048178] border-[#048178] text-white"
                : "bg-white border-slate-200 text-slate-700 hover:bg-[#E6F2F2] hover:text-[#048178] hover:border-[#048178]/30"
              }
            `}
          >
            <span className="w-5 flex justify-center items-center mr-2.5 text-base">
              {icon}
            </span>

            <span className="flex-1 text-xs 2xl:text-sm font-medium whitespace-nowrap">
              {text}
            </span>

            {alert && (
              <div
                className={`w-2 h-2 rounded-full ${active ? "bg-white" : "bg-[#048178]"
                  }`}
              />
            )}
          </div>
        </Link>
      </li>
    );
  }

  return (
    <li
      className={`
        relative flex items-center py-1 xl:py-2 px-3 my-1
        font-medium rounded-md cursor-pointer
        transition-colors group
        ${active == true
          ? "bg-gradient-to-tr from-indigo-200 to-indigo-100 text-indigo-800"
          : "hover:bg-indigo-50 text-gray-600"
        }
    `}
    >
      <Link to={link}>
        <span className=" w-4 text-xs 2xl:text-sm">{icon}</span>
      </Link>

      <span
        className={`overflow-hidden transition-all text-xs 2xl:text-sm whitespace-nowrap ${expanded
          ? `w-28 xl:w-32 ${i18n.language == "ar" ? "mr-3" : "ml-3"} `
          : "w-0"
          }`}
      >
        <Link to={link}>{text}</Link>
      </span>
      {alert && (
        <div
          className={`absolute right-2 w-2 h-2 rounded bg-indigo-400 ${expanded ? "" : "top-2"
            }`}
        />
      )}
    </li>
  );
}
